import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { Button, Heading, Input, Label } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import Group from 'goals/group-selector/Group';
import SingleGroupSelector from 'goals/group-selector/SingleGroupSelector';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';

export default function DepartmentDetails(props: { group: graphql.Group }) {
	const { t } = useTranslation();

	const [editing, setEditing] = useState(false);

	const [update] = graphql.useUpdateGroupMutation();
	const [move] = graphql.useMoveGroupMutation();

	const [error, attempt, reset] = useErrorBoundary();

	const formik = useFormik({
		initialValues: {
			name: props.group?.name ?? '',
			hris: props.group?.externalReferenceID ?? '',
			groupID: props.group?.parent?.id,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		formik.setValues({
			name: props.group?.name ?? '',
			hris: props.group?.externalReferenceID ?? '',
			groupID: props.group?.parent?.id,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!props.group, props.group?.id]);

	useEffect(() => {
		if (!props.group) {
			return;
		}

		reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(formik.values)]);

	useEffect(() => {
		if (error && !editing) {
			setEditing(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!error, editing]);

	return (
		<FormikProvider value={formik}>
			<Flex alignItems="center" mb={3}>
				<Heading flexGrow={1} flexShrink={1}>
					{t('settings.employees.personal.title')}
				</Heading>
				<Box flexGrow={0} flexShrink={0}>
					{editing ? (
						<Button
							type="primary"
							title={t('common.save')}
							onClick={attempt(async () => {
								if (!props.group) {
									return;
								}

								setEditing(false);
								await update({
									variables: {
										input: {
											id: props.group.id,
											name: formik.values.name,
											externalReferenceID: formik.values.hris ? formik.values.hris : undefined,
										},
									},
								});

								if (props.group.parent?.id !== formik.values.groupID) {
									await move({
										variables: {
											input: {
												id: props.group.id,
												newParentID: formik.values.groupID,
											},
										},
									});
								}
							})}
						/>
					) : (
						<Button
							type="transparent"
							disabled={!props.group.viewerCanUpdate}
							title={t('common.edit')}
							onClick={() => setEditing(true)}
						/>
					)}
				</Box>
			</Flex>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error} />
				</Box>
			)}
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.name')}:</Label>
				<Input.Formik key={`${editing}`} name="name" disabled={!editing} autoFocus={editing} />
			</Box>
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.hris')}:</Label>
				<Input.Formik key={`${editing}`} name="hris" disabled={!editing} autoFocus={editing} />
			</Box>
			<Box>
				<Label mb={2}>{t('settings.departments.details.parent')}:</Label>
				<SingleGroupSelector.Formik
					types={[graphql.GroupType.Department]}
					required
					disabled={!editing}
					name="groupID"
					canSelect={(group) => group.type === graphql.GroupType.Department}
				/>
			</Box>
		</FormikProvider>
	);
}

export function DepartmentDetailsSkeleton() {
	const { t } = useTranslation();

	return (
		<>
			<Flex alignItems="center" mb={3}>
				<Heading flexGrow={1} flexShrink={1}>
					{t('settings.department.details.title')}
				</Heading>
				<Box flexGrow={0} flexShrink={0}>
					<Button.Skeleton />
				</Box>
			</Flex>
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.name')}:</Label>
				<Input.Skeleton />
			</Box>
			<Box mb={2}>
				<Label mb={2}>{t('settings.departments.details.hris')}:</Label>
				<Input.Skeleton />
			</Box>
			<Box>
				<Label mb={2}>{t('settings.departments.details.parent')}:</Label>
				<Group.Skeleton />
			</Box>
		</>
	);
}

DepartmentDetails.Skeleton = DepartmentDetailsSkeleton;
